export const TILE_SIZE = 256;
export const MAX_TILE_ZOOM = 22;
const MAX_LATITUDE = 85.05112878;
const PLACEHOLDER = /\{([^}]*)\}/g;
function hasControlChar(s) {
    for (let i = 0; i < s.length; i += 1) {
        const c = s.charCodeAt(i);
        if (c < 0x20 || c === 0x7f)
            return true;
    }
    return false;
}
/** The stable issue of a tile source, or undefined when it is usable. */
export function tileSourceIssue(v) {
    const t = v.urlTemplate;
    if (typeof t !== 'string' || t.length === 0 || t.length > 400 || /\s/.test(t))
        return 'TILE_URL_INVALID';
    const names = [...t.matchAll(PLACEHOLDER)].map((m) => m[1]);
    if (names.length !== 3 || !['z', 'x', 'y'].every((n) => names.filter((m) => m === n).length === 1))
        return 'TILE_URL_PLACEHOLDERS';
    let url;
    try {
        url = new URL(t.replace('{z}', '0').replace('{x}', '0').replace('{y}', '0'));
    }
    catch {
        return 'TILE_URL_INVALID';
    }
    if (url.protocol !== 'https:' && url.protocol !== 'http:')
        return 'TILE_URL_INVALID';
    if (url.username !== '' || url.password !== '' || url.search !== '' || url.hash !== '' || t.includes('?') || t.includes('#'))
        return 'TILE_URL_QUERY_OR_CREDENTIALS';
    if (typeof v.attribution !== 'string' || v.attribution.trim().length === 0 || v.attribution.length > 200 || hasControlChar(v.attribution))
        return 'TILE_ATTRIBUTION_REQUIRED';
    const zoomOk = (z) => typeof z === 'number' && Number.isInteger(z) && z >= 0 && z <= MAX_TILE_ZOOM;
    if (!zoomOk(v.minZoom) || !zoomOk(v.maxZoom) || v.minZoom > v.maxZoom)
        return 'TILE_ZOOM_INVALID';
    return undefined;
}
/** The URL of one tile. */
export function tileUrl(template, z, x, y) {
    return template.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
}
/** World pixel coordinates of a point at a zoom. */
export function project(p, zoom) {
    const scale = TILE_SIZE * 2 ** zoom;
    const lat = Math.max(-MAX_LATITUDE, Math.min(MAX_LATITUDE, p.latitude));
    const sin = Math.sin((lat * Math.PI) / 180);
    return {
        x: ((p.longitude + 180) / 360) * scale,
        y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale,
    };
}
/** The point at world pixel coordinates. */
export function unproject(px, zoom) {
    const scale = TILE_SIZE * 2 ** zoom;
    const n = Math.PI - (2 * Math.PI * px.y) / scale;
    return { latitude: (180 / Math.PI) * Math.atan(Math.sinh(n)), longitude: (px.x / scale) * 360 - 180 };
}
/**
 * The centre and the highest zoom (within `[minZoom, maxZoom]`) that fit every point inside the viewport less `padding`
 * pixels on each side. No point → undefined.
 */
export function fitView(points, viewport, bounds, padding = 32) {
    if (points.length === 0)
        return undefined;
    const w = Math.max(1, viewport.width - 2 * padding);
    const h = Math.max(1, viewport.height - 2 * padding);
    for (let z = bounds.maxZoom; z >= bounds.minZoom; z -= 1) {
        const px = points.map((p) => project(p, z));
        const minX = Math.min(...px.map((p) => p.x));
        const maxX = Math.max(...px.map((p) => p.x));
        const minY = Math.min(...px.map((p) => p.y));
        const maxY = Math.max(...px.map((p) => p.y));
        if (maxX - minX <= w && maxY - minY <= h)
            return { center: unproject({ x: (minX + maxX) / 2, y: (minY + maxY) / 2 }, z), zoom: z };
    }
    const px = points.map((p) => project(p, bounds.minZoom));
    const cx = (Math.min(...px.map((p) => p.x)) + Math.max(...px.map((p) => p.x))) / 2;
    const cy = (Math.min(...px.map((p) => p.y)) + Math.max(...px.map((p) => p.y))) / 2;
    return { center: unproject({ x: cx, y: cy }, bounds.minZoom), zoom: bounds.minZoom };
}
/** Where a point lands on screen (pixels from the viewport's top-left) for a view. */
export function toScreen(p, view, viewport) {
    const c = project(view.center, view.zoom);
    const q = project(p, view.zoom);
    return { left: q.x - c.x + viewport.width / 2, top: q.y - c.y + viewport.height / 2 };
}
/** The point under a screen position for a view (the inverse of {@link toScreen}). */
export function fromScreen(pos, view, viewport) {
    const c = project(view.center, view.zoom);
    return unproject({ x: c.x + pos.left - viewport.width / 2, y: c.y + pos.top - viewport.height / 2 }, view.zoom);
}
/** The tiles covering the viewport for a view (rows outside the world are skipped, columns wrap). */
export function visibleTiles(view, viewport) {
    const z = view.zoom;
    const n = 2 ** z;
    const c = project(view.center, z);
    const originX = c.x - viewport.width / 2;
    const originY = c.y - viewport.height / 2;
    const x0 = Math.floor(originX / TILE_SIZE);
    const x1 = Math.floor((originX + viewport.width - 1) / TILE_SIZE);
    const y0 = Math.max(0, Math.floor(originY / TILE_SIZE));
    const y1 = Math.min(n - 1, Math.floor((originY + viewport.height - 1) / TILE_SIZE));
    const out = [];
    for (let ty = y0; ty <= y1; ty += 1) {
        for (let tx = x0; tx <= x1; tx += 1) {
            const wrapped = ((tx % n) + n) % n;
            out.push({ z, x: wrapped, y: ty, left: tx * TILE_SIZE - originX, top: ty * TILE_SIZE - originY, key: `${z}/${tx}/${ty}` });
        }
    }
    return out;
}
/** Clamp a zoom into a source's range. */
export function clampZoom(zoom, source) {
    return Math.max(source.minZoom, Math.min(source.maxZoom, Math.round(zoom)));
}
//# sourceMappingURL=slippy.js.map